import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Tag } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";

const EMPTY = {
  code: "",
  type: "percent",
  value: 10,
  min_subtotal: 0,
  max_uses: "",
  expires_at: "",
  active: true,
};

function DiscountForm({ open, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.code);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        ...EMPTY,
        ...initial,
        max_uses: initial.max_uses ?? "",
        expires_at: initial.expires_at ? initial.expires_at.slice(0, 10) : "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [initial, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        code: (form.code || "").trim().toUpperCase(),
        type: form.type,
        value: Number(form.value) || 0,
        min_subtotal: Number(form.min_subtotal) || 0,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59Z").toISOString() : null,
        active: !!form.active,
      };
      await api.post("/admin/discounts", payload);
      toast.success(isEdit ? "Discount updated" : "Discount created");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#1F1F1F] border-white/10 text-[#F5F5F5]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? `Edit ${initial.code}` : "New Discount Code"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="col-span-2">
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Code</label>
            <Input
              data-testid="discount-form-code"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              disabled={isEdit}
              placeholder="SAVE10"
              className="font-mono"
            />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Type</label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger data-testid="discount-form-type" className="bg-transparent border-white/20 text-[#F5F5F5]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1F1F1F] border-white/10 text-[#F5F5F5]">
                <SelectItem value="percent">Percent (%)</SelectItem>
                <SelectItem value="fixed">Fixed (USD)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">
              Value {form.type === "percent" ? "(%)" : "($)"}
            </label>
            <Input data-testid="discount-form-value" type="number" step="0.01" value={form.value} onChange={set("value")} />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Min Subtotal ($)</label>
            <Input data-testid="discount-form-min" type="number" step="0.01" value={form.min_subtotal} onChange={set("min_subtotal")} />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Max Uses (blank = ∞)</label>
            <Input data-testid="discount-form-max" type="number" value={form.max_uses} onChange={set("max_uses")} />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Expires (optional)</label>
            <Input data-testid="discount-form-expires" type="date" value={form.expires_at} onChange={set("expires_at")} />
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="discount-form-active" checked={!!form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Active</label>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            data-testid="discount-form-save"
            onClick={submit}
            disabled={saving || !form.code || !form.value}
            className="bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998]"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Save Changes" : "Create Code")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DiscountsTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/discounts");
      setCodes(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async () => {
    try {
      await api.delete(`/admin/discounts/${toDelete.code}`);
      toast.success(`Deleted ${toDelete.code}`);
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4ADE80]" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-[#A8A8A8]">{codes.length} discount codes</div>
        <Button data-testid="admin-new-discount-btn" onClick={() => setCreating(true)} className="bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998]">
          <Plus className="h-4 w-4 mr-2" /> New Code
        </Button>
      </div>
      <div className="border border-white/10 bg-[#1F1F1F] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8]">
            <tr className="border-b border-white/10">
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-right px-4 py-3">Value</th>
              <th className="text-right px-4 py-3">Min Subtotal</th>
              <th className="text-right px-4 py-3">Uses</th>
              <th className="text-left px-4 py-3">Expires</th>
              <th className="text-center px-4 py-3">Active</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-[#A8A8A8]">
                <Tag className="h-8 w-8 mx-auto mb-2 opacity-40" />
                No discount codes yet. Create your first one.
              </td></tr>
            )}
            {codes.map((c) => (
              <tr key={c.code} data-testid={`admin-discount-row-${c.code}`} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => setEditing(c)}>
                <td className="px-4 py-3 font-mono text-[#4ADE80]">{c.code}</td>
                <td className="px-4 py-3 text-[#A8A8A8]">{c.type}</td>
                <td className="px-4 py-3 text-right font-mono text-[#F5F5F5]">
                  {c.type === "percent" ? `${c.value}%` : `$${Number(c.value).toFixed(2)}`}
                </td>
                <td className="px-4 py-3 text-right font-mono text-[#A8A8A8]">
                  {c.min_subtotal ? `$${Number(c.min_subtotal).toFixed(2)}` : "—"}
                </td>
                <td className="px-4 py-3 text-right font-mono text-[#F5F5F5]">
                  {c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}
                </td>
                <td className="px-4 py-3 text-[#A8A8A8]">{c.expires_at ? c.expires_at.slice(0, 10) : "—"}</td>
                <td className="px-4 py-3 text-center">
                  {c.active
                    ? <Badge className="bg-[#4ADE80]/20 text-[#4ADE80] border-0">Active</Badge>
                    : <Badge className="bg-white/10 text-[#A8A8A8] border-0">Disabled</Badge>}
                </td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <Button data-testid={`admin-delete-discount-${c.code}`} size="sm" variant="ghost" onClick={() => setToDelete(c)}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DiscountForm open={creating} initial={null} onClose={() => setCreating(false)} onSaved={load} />
      <DiscountForm open={!!editing} initial={editing} onClose={() => setEditing(null)} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#1F1F1F] border-white/10 text-[#F5F5F5]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.code}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A8A8A8]">
              This will permanently remove the code. New checkouts using it will fail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border border-white/20 text-[#F5F5F5] hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-confirm-delete-discount" onClick={del} className="bg-red-500/80 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
